// Hausmeister-App (Portale-Redesign E2) — eigene, bewusst schmale Oberfläche.
//
// Sie zeigt ausschließlich freigegebene Tickets und zugewiesene Übergaben; die Datenbank gibt
// gar nichts anderes heraus (ticket_freigegeben, uebergabe_freigegeben). Optik nach
// design_handoff_portale (Tabs Übersicht · Aufträge · Übergaben); Laden, Speichern, Timer,
// Arbeitszeit und Übergabe-Start sind dieselben Aufrufe wie vorher.
import React, { useCallback, useEffect, useState } from 'react';
import { CaretLeft, CircleNotch, ClipboardText, Gauge, Key, Play, SignOut, SquaresFour, StopCircle } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { ladeMeineTickets, type HausmeisterTicket } from '../../lib/hausmeister/api';
import { hm, offeneAnzahl } from '../../lib/hausmeister/darstellung';
import { bereiteUebergabeVor, ladeMeineUebergaben, type HausmeisterUebergabe } from '../../lib/hausmeister/uebergaben';
import { useZeit } from '../../contexts/ZeitContext';
import UebergabeAssistent from '../admin/uebergabe/UebergabeAssistent';
import { PortalAvatar, PortalShell, PortalTabs, type PortalTab } from '../../components/portal/PortalShell';
import { FehlerHinweis } from '../../components/portal/bausteine';
import Uebersicht from './Uebersicht';
import AuftragListe from './AuftragListe';
import AuftragDetail from './AuftragDetail';
import UebergabenListe from './UebergabenListe';
import ZaehlerBereich from './ZaehlerBereich';
import { darfZaehler } from '../../lib/hausmeister/zaehler';

interface Props {
  userProfile?: { id?: string; full_name?: string; email?: string } | null;
}

type Tab = 'start' | 'auftraege' | 'uebergaben' | 'zaehler';

const HausmeisterApp: React.FC<Props> = ({ userProfile }) => {
  const meineId = userProfile?.id || '';
  const name = userProfile?.full_name || 'Hausmeister';
  const zeit = useZeit();
  const [tab, setTab] = useState<Tab>('start');
  const [tickets, setTickets] = useState<HausmeisterTicket[]>([]);
  const [offen, setOffen] = useState<HausmeisterTicket | null>(null);
  // Übergaben, die das Büro freigegeben hat (Ticket #436)
  const [uebergaben, setUebergaben] = useState<HausmeisterUebergabe[]>([]);
  const [uebergabeOffen, setUebergabeOffen] = useState<HausmeisterUebergabe | null>(null);
  const [bereitetVor, setBereitetVor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  // Meldungen des Assistenten nach dem Schließen (Abschluss ok / Problem) – statt Browser-Popup
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);
  const [stempelt, setStempelt] = useState(false);
  // #616: Reiter „Zähler“ nur mit dem Modul „Zähler“ (Team → Berechtigungen) – die Datenbank entscheidet
  const [mitZaehlern, setMitZaehlern] = useState(false);
  useEffect(() => { void darfZaehler().then(setMitZaehlern); }, []);

  const laden = useCallback(async () => {
    setLaedt(true);
    const [t, u] = await Promise.all([ladeMeineTickets(), ladeMeineUebergaben(meineId)]);
    setTickets(t);
    setUebergaben(u);
    setLaedt(false);
  }, [meineId]);

  const uebergabeStarten = async (u: HausmeisterUebergabe) => {
    setBereitetVor(u.protokollId);
    setFehler(null);
    try {
      const fehlend = await bereiteUebergabeVor(u, userProfile);
      if (fehlend > 0) setFehler(`${fehlend} Foto(s) der Vorlage ließen sich nicht laden — der Rest ist übernommen.`);
      setUebergabeOffen(u);
    } catch (e) {
      setFehler(`Übergabe konnte nicht vorbereitet werden: ${(e as Error).message}`);
    } finally {
      setBereitetVor(null);
    }
  };
  useEffect(() => { void laden(); }, [laden]);

  const oeffnen = (t: HausmeisterTicket) => {
    setFehler(null);
    setMeldung(null);
    setOffen(t);
    window.scrollTo(0, 0);
  };
  const zurueck = () => { setOffen(null); window.scrollTo(0, 0); };
  const wechsel = (k: Tab) => { setTab(k); setOffen(null); window.scrollTo(0, 0); };

  // Kommen/Feierabend wie bisher über den ZeitContext; scheitert das Speichern, gibt es jetzt einen Hinweis.
  const stempeln = async () => {
    if (stempelt) return;
    setStempelt(true);
    const ok = await (zeit.laufend ? zeit.beenden() : zeit.starten());
    setStempelt(false);
    if (!ok) setFehler('Die Arbeitszeit konnte nicht gespeichert werden. Bitte noch einmal versuchen.');
  };

  const eingestempelt = !!zeit.laufend;
  const tabs: PortalTab<Tab>[] = [
    { key: 'start', label: 'Übersicht', icon: SquaresFour, tip: 'Übersicht mit Arbeitszeit und Kennzahlen' },
    { key: 'auftraege', label: 'Aufträge', icon: ClipboardText, tip: 'Alle freigegebenen Aufträge', anzahl: offeneAnzahl(tickets) },
    { key: 'uebergaben', label: 'Übergaben', icon: Key, tip: 'Mir zugewiesene Wohnungsübergaben', anzahl: uebergaben.length },
    ...(mitZaehlern ? [{ key: 'zaehler' as const, label: 'Zähler', icon: Gauge, tip: 'Zähler ansehen, Stände erfassen, neue Zähler anlegen' }] : []),
  ];

  const uebergabeSchliessen = () => { setUebergabeOffen(null); void laden(); window.scrollTo(0, 0); };

  const inhalt = (): React.ReactNode => {
    // Der Übergabe-Assistent ist derselbe wie im Büro — offline-fähig, fürs Telefon gebaut.
    // In der dunklen Portal-Fläche übernimmt er die Tokens aus data-theme="dark".
    if (uebergabeOffen) {
      return (
        <UebergabeAssistent
          protokollId={uebergabeOffen.protokollId}
          userProfile={userProfile}
          bezug={uebergabeOffen.bezug}
          fuerHausmeister
          meldeDanach={(text, istFehler) => setMeldung({ text, fehler: !!istFehler })}
          onFertig={uebergabeSchliessen}
          onAbbrechen={uebergabeSchliessen}
        />
      );
    }
    if (laedt && !tickets.length && !uebergaben.length) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--p-w40)' }} aria-busy="true" aria-label="Lädt">
          <CircleNotch size={24} weight="bold" className="animate-spin" />
        </div>
      );
    }
    if (offen) {
      return <AuftragDetail key={offen.id} ticket={offen} meineId={meineId} onFehler={setFehler} onGeaendert={() => { void laden(); }} />;
    }
    if (tab === 'auftraege') return <AuftragListe tickets={tickets} jetzt={zeit.jetzt} onOeffnen={oeffnen} />;
    if (tab === 'zaehler' && mitZaehlern) {
      return <ZaehlerBereich onFehler={setFehler} onMeldung={(text) => setMeldung({ text, fehler: false })} />;
    }
    if (tab === 'uebergaben') {
      return <UebergabenListe uebergaben={uebergaben} bereitetVor={bereitetVor} onOeffnen={(u) => void uebergabeStarten(u)} />;
    }
    return (
      <Uebersicht
        meineId={meineId}
        vollerName={name}
        tickets={tickets}
        uebergaben={uebergaben}
        bereitetVor={bereitetVor}
        onAuftrag={oeffnen}
        onAlleAuftraege={() => wechsel('auftraege')}
        onUebergabe={(u) => void uebergabeStarten(u)}
        onFehler={setFehler}
      />
    );
  };

  let chipSymbol: React.ReactNode = <Play size={17} weight="bold" />;
  if (stempelt) chipSymbol = <CircleNotch size={17} weight="bold" className="animate-spin" />;
  else if (eingestempelt) chipSymbol = <StopCircle size={17} weight="fill" />;

  const zeitChip = (
    <button
      type="button"
      onClick={() => void stempeln()}
      disabled={stempelt || !zeit.bereit}
      className={`p-zeitchip ${eingestempelt ? 'p-zeitchip-an' : 'p-zeitchip-aus'}`}
      data-tip={eingestempelt
        ? 'Arbeitszeit läuft seit Schichtbeginn · Klicken zum Ausstempeln (Feierabend)'
        : 'Nicht eingestempelt · Klicken zum Einstempeln'}
      aria-label={eingestempelt ? `Arbeitszeit ${hm(zeit.laufendeMinuten)} Stunden – ausstempeln` : 'Einstempeln'}
    >
      {chipSymbol}
      <span className="p-zeitchip-text">{eingestempelt ? `${hm(zeit.laufendeMinuten)} h` : 'Kommen'}</span>
    </button>
  );

  const abmelden = (
    <button type="button" className="p-rund" onClick={() => void supabase.auth.signOut()} data-tip="Abmelden" aria-label="Abmelden"
      style={{ background: 'transparent', color: 'var(--p-w45)' }}>
      <SignOut size={19} />
    </button>
  );

  return (
    <PortalShell
      breite={offen || uebergabeOffen ? 'schmal' : 'weit'}
      links={offen || uebergabeOffen ? (
        <button type="button" className="p-rund" onClick={uebergabeOffen ? uebergabeSchliessen : zurueck}
          data-tip={uebergabeOffen ? 'Zurück zur Liste – der Entwurf bleibt gespeichert' : 'Zurück'} aria-label="Zurück">
          <CaretLeft size={18} weight="bold" />
        </button>
      ) : (
        <PortalAvatar name={name} tip={`${name} · Hausmeister`} punkt={eingestempelt ? 'an' : 'aus'} />
      )}
      mitte={offen || uebergabeOffen ? undefined : <PortalTabs label="Bereiche" tabs={tabs} aktiv={tab} onWahl={wechsel} />}
      rechts={<>{zeitChip}{abmelden}</>}
    >
      {fehler && <FehlerHinweis text={fehler} onSchliessen={() => setFehler(null)} />}
      {meldung && !uebergabeOffen && (
        <FehlerHinweis text={meldung.text} ton={meldung.fehler ? 'fehler' : 'ok'} onSchliessen={() => setMeldung(null)} />
      )}

      {inhalt()}
    </PortalShell>
  );
};

export default HausmeisterApp;
